import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { getProfessional, IProfessionalProfile } from "../api/getProfessional";
import Button from "../components/buttons/Button";
import OfferedProcedures from "../components/common/OfferedProcedures";
import PageWrapper from "../components/common/PageWrapper";
import ProfessionalSubEspecialty from "../components/common/ProfessionalSubEspecialty";
import StarIndicator from "../components/common/StarIndicator";
import ProfessionalProfileScreenLoader from "../components/loaders/ProfessionalProfileScreenLoader";

const ProfessionalProfileWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	box-sizing: border-box;
	height: 100%;
	width: 100%;
	padding: 0px 28px;
`;

const ProfessionalAvatar = styled.img`
	width: 130px;
	height: 130px;
	border-radius: 130px;
	margin-top: 50px;
`;

const ProfessionalName = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: bold;
	font-size: 17px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	margin-top: 5px;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
`;

const BoardData = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.brownVitat || "#959aab"};
`;

const StarIndicatorWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: normal;
`;

const ProfessionalBio = styled.p`
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 12.5px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: #000000;
	margin: 16px 0px;
`;

const Subspecialties = styled(ProfessionalSubEspecialty)`
	margin: 0;
	margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: normal;
	margin-top: 28px;
	margin-bottom: 16px;
`;

/**
 * ### Tela de perfil do profissional
 * Mostra informações basicas sobre profissional e sua area de atuação.
 */
const ProfessionalProfileScreen: FC = () => {
	const { professionalId } = useParams<{ professionalId: string }>();
	const [professional, setProfessional] = useState<IProfessionalProfile>();
	const navigate = useNavigate();

	useEffect(() => {
		if (professionalId) carregaDados(professionalId);
	}, [professionalId]);

	const carregaDados = async (codProfessional: string) => {
		setProfessional(undefined);
		const professionalResponse: IProfessionalProfile = await getProfessional(codProfessional);
		setProfessional(professionalResponse);
	};

	const handleOpenReviews = () => {
		navigate(`/medicos/${professionalId}/reviews`);
	};

	const handleOpenAvailableTimes = () => {
		if (!professional) return;
		navigate(`/medicos/${professionalId}/agendamentos`);
	};

	if (professional) {
		return (
			<PageWrapper>
				<ProfessionalProfileWrapper>
					<ProfessionalAvatar src={professional.imagem} />
					<ProfessionalName>{professional.nome}</ProfessionalName>
					<BoardData>
						{professional.especialidade || ""} {professional.registro && " - " + professional.registro + " " + professional.numeroRegistro}
					</BoardData>
					<StarIndicatorWrapper>
						<StarIndicator
							grade={professional.avaliacao}
							maxGrade={5}
							reviewsQuantidade={professional.contagemAvaliacao}
							handleTotalReviewsClick={handleOpenReviews}
						/>
					</StarIndicatorWrapper>
					<ProfessionalBio>{professional.biografia}</ProfessionalBio>

					{professional.subespecialidade && professional.subespecialidade.length > 0 && (
						<Subspecialties>
							<b>Subespecialidades:</b> {professional.subespecialidade.join(", ")}
						</Subspecialties>
					)}

					{/* {professional.procedimentos.length > 0 && (
						<OfferedProcedures>
							<b>{professional.procedimentos.length > 1 ? "Procedimentos oferecidos: " : "Procedimento oferecido: "}</b>
							{professional.procedimentos.map(procedimento => procedimento.description).join(", ")}
						</OfferedProcedures>
					)} */}

					<ButtonWrapper>
						<Button onClick={handleOpenAvailableTimes}>Ver horários disponíveis</Button>
					</ButtonWrapper>
				</ProfessionalProfileWrapper>
			</PageWrapper>
		);
	} else {
		return (
			<ProfessionalProfileWrapper>
				<ProfessionalProfileScreenLoader />
			</ProfessionalProfileWrapper>
		);
	}
};

export default ProfessionalProfileScreen;
